import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Download, FileText, BarChart2, TrendingUp, AlertTriangle,
  Users, PlayCircle, Loader2, ChevronDown, Check, X,
  Filter, RefreshCw, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#09D6B4';

// ── Helpers ────────────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { value: 'call_log', label: 'Call Log', icon: FileText, desc: 'All calls with scores & status' },
  { value: 'agent_summary', label: 'Agent Summary', icon: Users, desc: 'Per-agent aggregated performance' },
  { value: 'escalation_report', label: 'Escalations', icon: AlertTriangle, desc: 'Calls below threshold' },
  { value: 'criteria_breakdown', label: 'Criteria Breakdown', icon: BarChart2, desc: 'Pass/fail rates per criterion' },
  { value: 'performance_trend', label: 'Trend Analysis', icon: TrendingUp, desc: 'Weekly performance over time' },
];

const CALL_TYPES = [
  'all', 'car_dealership', 'real_estate', 'legal_firm', 'it_support',
  'customer_support', 'service_department', 'dental_clinic',
  'finance_mortgage', 'insurance_broker', 'construction',
  'education_training', 'recruitment', 'property_management',
  'b2b_sales', 'fitness_gym', 'emergency_services',
  'manager_leadership', 'admin_accounts', 'admin_general', 'manager_other',
  'other',
];

const fmtLabel = v => v === 'all' ? 'All Types' : v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const scoreColor = s => {
  if (typeof s !== 'number') return '';
  if (s >= 80) return 'text-emerald-600';
  if (s >= 65) return 'text-amber-600';
  return 'text-rose-600';
};

const statusBadge = s => {
  const map = {
    analyzed: 'bg-emerald-100 text-emerald-700',
    escalated: 'bg-rose-100 text-rose-700',
    pending: 'bg-amber-100 text-amber-700',
    skipped_short: 'bg-slate-100 text-slate-500',
  };
  return map[s] || 'bg-slate-100 text-slate-600';
};

const downloadCSV = (rows, columns, filename) => {
  if (!rows.length) { toast.error('No data to export'); return; }
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [columns.map(esc).join(','), ...rows.map(r => columns.map(c => esc(r[c])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('CSV downloaded');
};

const exportPDF = (rows, columns, reportType, summary, filters) => {
  if (!rows.length) { toast.error('No data to export'); return; }
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });

  // Header
  doc.setFillColor(10, 22, 40);
  doc.rect(0, 0, doc.internal.pageSize.width, 28, 'F');
  doc.setTextColor(93, 190, 205);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Yabbit VoiceFabric', 14, 13);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 200, 210);
  const label = REPORT_TYPES.find(r => r.value === reportType)?.label || reportType;
  doc.text(`${label} Report  ·  Generated ${new Date().toLocaleString()}`, 14, 22);

  // Summary pills
  let y = 38;
  if (summary) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    const entries = Object.entries(summary).slice(0, 4);
    entries.forEach(([k, v], i) => {
      const x = 14 + i * 50;
      doc.setFillColor(240, 253, 250);
      doc.roundedRect(x, y - 5, 46, 12, 2, 2, 'F');
      doc.setTextColor(93, 190, 205);
      doc.setFont('helvetica', 'bold');
      doc.text(String(v), x + 4, y + 1);
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.text(k.replace(/_/g, ' '), x + 4, y + 6);
    });
    y = 58;
  }

  // Active filters note
  const activeFilters = [];
  if (filters.dateFrom) activeFilters.push(`From: ${filters.dateFrom}`);
  if (filters.dateTo) activeFilters.push(`To: ${filters.dateTo}`);
  if (filters.callType && filters.callType !== 'all') activeFilters.push(`Type: ${fmtLabel(filters.callType)}`);
  if (filters.callStatus && filters.callStatus !== 'all') activeFilters.push(`Status: ${filters.callStatus}`);
  if (filters.scoreMin > 0 || filters.scoreMax < 100) activeFilters.push(`Score: ${filters.scoreMin}–${filters.scoreMax}`);
  if (activeFilters.length) {
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text(`Filters applied: ${activeFilters.join('  |  ')}`, 14, y);
    y += 8;
  }

  // Table
  autoTable(doc, {
    head: [columns],
    body: rows.map(r => columns.map(c => {
      const v = r[c];
      return v === null || v === undefined ? '' : String(v);
    })),
    startY: y,
    styles: { fontSize: 7.5, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [10, 22, 40], textColor: [93, 190, 205], fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {},
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      // Footer on each page
      const pageH = doc.internal.pageSize.height;
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Yabbit VoiceFabric  ·  Page ${data.pageNumber}`,
        doc.internal.pageSize.width / 2,
        pageH - 8,
        { align: 'center' }
      );
    },
  });

  doc.save(`yabbit-${reportType}-${new Date().toISOString().slice(0, 10)}.pdf`);
  toast.success('PDF downloaded');
};

// ── Agent Picker ───────────────────────────────────────────────────────────────
const AgentPicker = ({ agents, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleAgent = (id) => {
    onChange(selected.includes(id) ? selected.filter(a => a !== id) : [...selected, id]);
  };

  const label = selected.length === 0
    ? 'All agents'
    : selected.length === 1
      ? agents.find(a => a.id === selected[0])?.name || '1 agent'
      : `${selected.length} agents`;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:border-slate-300 focus:outline-none min-w-[140px] justify-between"
        data-testid="agent-picker-btn"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl max-h-52 overflow-y-auto">
          <div className="p-1.5">
            <button
              onClick={() => { onChange([]); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 rounded text-sm hover:bg-slate-50 ${selected.length === 0 ? 'text-teal-600 font-medium' : 'text-slate-600'}`}
            >
              {selected.length === 0 && <Check className="w-3.5 h-3.5" />}
              {selected.length > 0 && <span className="w-3.5" />}
              All agents
            </button>
            {agents.map(a => (
              <button
                key={a.id}
                onClick={() => toggleAgent(a.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 rounded text-sm hover:bg-slate-50 text-slate-700"
              >
                {selected.includes(a.id)
                  ? <Check className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                  : <span className="w-3.5 flex-shrink-0" />
                }
                <span className="truncate">{a.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Summary Cards ──────────────────────────────────────────────────────────────
const SummaryCards = ({ summary, reportType }) => {
  if (!summary) return null;

  const icons = {
    call_log: [FileText, Users, BarChart2, AlertTriangle],
    agent_summary: [Users, FileText, BarChart2, AlertTriangle],
    escalation_report: [AlertTriangle, BarChart2, Users, FileText],
    criteria_breakdown: [BarChart2, Check, X, FileText],
    performance_trend: [Users, TrendingUp, BarChart2, FileText],
  };
  const iconsForType = icons[reportType] || [FileText, FileText, FileText, FileText];

  const entries = Object.entries(summary);
  const colors = ['from-teal-500/10 to-teal-500/5', 'from-blue-500/10 to-blue-500/5', 'from-amber-500/10 to-amber-500/5', 'from-rose-500/10 to-rose-500/5'];
  const iconColors = ['text-teal-500', 'text-blue-500', 'text-amber-500', 'text-rose-500'];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {entries.slice(0, 4).map(([key, val], i) => {
        const Icon = iconsForType[i] || FileText;
        return (
          <div key={key} className={`bg-gradient-to-br ${colors[i]} border border-white rounded-xl p-4`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 capitalize mb-1">{key.replace(/_/g, ' ')}</p>
                <p className={`text-2xl font-bold text-slate-800 ${typeof val === 'string' && val.length > 12 ? 'text-sm leading-tight mt-1' : ''}`}>
                  {typeof val === 'number' ? val.toLocaleString() : val}
                </p>
              </div>
              <Icon className={`w-4 h-4 ${iconColors[i]} mt-0.5`} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Data Table ─────────────────────────────────────────────────────────────────
const DataTable = ({ columns, rows, reportType }) => {
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (!rows.length) return (
    <div className="text-center py-12 text-slate-400">
      <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm">No records match your filters</p>
    </div>
  );

  const renderCell = (col, val, reportType) => {
    if (col === 'Score' || col === 'Avg Score' || col === 'Overall Avg') {
      return <span className={`font-semibold ${scoreColor(val)}`}>{val}</span>;
    }
    if (col === 'Status') {
      return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(val)}`}>{val}</span>;
    }
    if (col === 'Pass Rate %') {
      const n = parseFloat(val);
      return <span className={n >= 70 ? 'text-emerald-600 font-semibold' : n >= 50 ? 'text-amber-600 font-semibold' : 'text-rose-600 font-semibold'}>{val}%</span>;
    }
    if (col === 'Fail Rate %') {
      const n = parseFloat(val);
      return <span className={n <= 30 ? 'text-emerald-600 font-semibold' : n <= 50 ? 'text-amber-600 font-semibold' : 'text-rose-600 font-semibold'}>{val}%</span>;
    }
    if (typeof val === 'number' && col.startsWith('202')) {
      // Week columns in trend report
      return <span className={scoreColor(val)}>{val}</span>;
    }
    return <span className="text-slate-700">{String(val ?? '')}</span>;
  };

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm min-w-max">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {columns.map(h => (
                <th key={h} className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr key={`report-row-${page}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors">
                {columns.map(col => (
                  <td key={col} className="py-2.5 px-3 whitespace-nowrap max-w-xs truncate" title={String(row[col] ?? '')}>
                    {renderCell(col, row[col], reportType)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
          <span>{rows.length} total records · Page {page + 1} of {totalPages}</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 text-xs">
              Prev
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}
              className="px-3 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 text-xs">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main ReportsTab ────────────────────────────────────────────────────────────
const ReportsTab = ({ domainId = null }) => {
  const { token, userIndustry } = useAuth();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [reportType, setReportType] = useState('call_log');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    selectedAgents: [],
    scoreMin: 0,
    scoreMax: 100,
    callStatus: 'all',
    callType: 'all',
  });
  const [agents, setAgents] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [running, setRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  // Load agent list for picker
  useEffect(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    else if (userIndustry) p.set('call_type', userIndustry);
    axios.get(`${API}/analytics/team${p.toString() ? `?${p}` : ''}`, { headers })
      .then(r => {
        const lb = r.data?.leaderboard || [];
        setAgents(lb.map(a => ({ id: a.agent_id, name: a.agent_name })));
      })
      .catch(() => {});
  }, [domainId, userIndustry, headers]);

  const runReport = async () => {
    setRunning(true);
    try {
      const p = new URLSearchParams({ report_type: reportType });
      if (domainId) p.set('domain_id', domainId);
      else if (userIndustry && filters.callType === 'all') p.set('call_type', userIndustry);

      if (filters.dateFrom) p.set('date_from', filters.dateFrom);
      if (filters.dateTo) p.set('date_to', filters.dateTo);
      if (filters.selectedAgents.length) p.set('agents', filters.selectedAgents.join(','));
      if (filters.scoreMin > 0) p.set('score_min', filters.scoreMin);
      if (filters.scoreMax < 100) p.set('score_max', filters.scoreMax);
      if (filters.callStatus && filters.callStatus !== 'all') p.set('call_status', filters.callStatus);
      if (filters.callType && filters.callType !== 'all') p.set('call_type', filters.callType);

      const res = await axios.get(`${API}/analytics/custom-report?${p}`, { headers });
      setReportData(res.data);
      setHasRun(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setRunning(false);
    }
  };

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const activeFilterCount = [
    filters.dateFrom, filters.dateTo,
    filters.selectedAgents.length > 0,
    filters.scoreMin > 0 || filters.scoreMax < 100,
    filters.callStatus !== 'all',
    filters.callType !== 'all',
  ].filter(Boolean).length;

  return (
    <div className="space-y-5" data-testid="reports-tab">

      {/* ── Report Type Selector ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {REPORT_TYPES.map(({ value, label, icon: Icon, desc }) => (
          <button
            key={value}
            onClick={() => { setReportType(value); setReportData(null); setHasRun(false); }}
            data-testid={`report-type-${value}`}
            className={`flex flex-col items-start gap-1.5 p-3 rounded-xl border text-left transition-all ${
              reportType === value
                ? 'border-teal-400 bg-teal-50 shadow-sm shadow-teal-100'
                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <div className={`p-1.5 rounded-lg ${reportType === value ? 'bg-teal-100' : 'bg-slate-100'}`}>
              <Icon className={`w-3.5 h-3.5 ${reportType === value ? 'text-teal-600' : 'text-slate-500'}`} />
            </div>
            <p className={`text-xs font-semibold ${reportType === value ? 'text-teal-700' : 'text-slate-700'}`}>{label}</p>
            <p className={`text-xs leading-tight ${reportType === value ? 'text-teal-500' : 'text-slate-400'}`}>{desc}</p>
          </button>
        ))}
      </div>

      {/* ── Filters Panel ────────────────────────────────────────────────── */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-teal-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            {/* Date From */}
            <div>
              <label className="text-xs text-slate-500 block mb-1">From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => setFilter('dateFrom', e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-100"
                data-testid="filter-date-from"
              />
            </div>
            {/* Date To */}
            <div>
              <label className="text-xs text-slate-500 block mb-1">To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => setFilter('dateTo', e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-100"
                data-testid="filter-date-to"
              />
            </div>
            {/* Agent picker */}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Agent</label>
              <AgentPicker
                agents={agents}
                selected={filters.selectedAgents}
                onChange={v => setFilter('selectedAgents', v)}
              />
            </div>
            {/* Call Type */}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Call Type</label>
              <Select value={filters.callType} onValueChange={v => setFilter('callType', v)}>
                <SelectTrigger className="w-44 text-sm" data-testid="filter-call-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CALL_TYPES.map(t => <SelectItem key={t} value={t}>{fmtLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Status */}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Status</label>
              <Select value={filters.callStatus} onValueChange={v => setFilter('callStatus', v)}>
                <SelectTrigger className="w-36 text-sm" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="analyzed">Analyzed</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Score Range */}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Score Range</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={0} max={100} value={filters.scoreMin}
                  onChange={e => setFilter('scoreMin', Math.min(parseInt(e.target.value) || 0, filters.scoreMax))}
                  className="w-14 px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm text-center text-slate-700 focus:outline-none focus:border-teal-400"
                  data-testid="filter-score-min"
                />
                <span className="text-slate-400 text-xs">to</span>
                <input
                  type="number" min={0} max={100} value={filters.scoreMax}
                  onChange={e => setFilter('scoreMax', Math.max(parseInt(e.target.value) || 100, filters.scoreMin))}
                  className="w-14 px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm text-center text-slate-700 focus:outline-none focus:border-teal-400"
                  data-testid="filter-score-max"
                />
              </div>
            </div>
            {/* Actions */}
            <div className="flex items-end gap-2 ml-auto">
              {activeFilterCount > 0 && (
                <button
                  onClick={() => setFilters({ dateFrom: '', dateTo: '', selectedAgents: [], scoreMin: 0, scoreMax: 100, callStatus: 'all', callType: 'all' })}
                  className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
              <Button
                onClick={runReport}
                disabled={running}
                className="bg-teal-500 hover:bg-teal-400 text-white px-5 gap-2"
                data-testid="run-report-btn"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                {running ? 'Generating...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Results ──────────────────────────────────────────────────────── */}
      {running && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
          <p className="text-sm text-slate-500">Building report…</p>
        </div>
      )}

      {!running && !hasRun && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
          <BarChart2 className="w-12 h-12 opacity-20" />
          <p className="text-sm font-medium">Select filters and click <span className="text-teal-600 font-semibold">Run Report</span> to generate</p>
        </div>
      )}

      {!running && reportData && (
        <div className="space-y-4">
          {/* Report header + export */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-slate-800">
                {REPORT_TYPES.find(r => r.value === reportType)?.label}
              </h2>
              <Badge variant="secondary" className="text-xs">
                {reportData.total_records.toLocaleString()} records
              </Badge>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => downloadCSV(reportData.rows, reportData.columns, `yabbit-${reportType}-${new Date().toISOString().slice(0,10)}.csv`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                data-testid="export-csv-btn"
              >
                <FileDown className="w-3.5 h-3.5" /> CSV
              </button>
              <button
                onClick={() => exportPDF(reportData.rows, reportData.columns, reportType, reportData.summary, filters)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 transition-colors"
                data-testid="export-pdf-btn"
              >
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                data-testid="print-report-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Print
              </button>
            </div>
          </div>

          {/* Summary cards */}
          <SummaryCards summary={reportData.summary} reportType={reportType} />

          {/* Data table */}
          <DataTable
            columns={reportData.columns}
            rows={reportData.rows}
            reportType={reportType}
          />
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
